import * as React from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Alert from 'antd/lib/alert';
import { TRY_GROUP_ISSUES, TriageViewType, tryGroupIssues } from '@/actions/triage';
import { IssueEntity } from '@/types';
import { runwayColors, rwColors } from '@/components/styles/globalStyles';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { FixRequestFormValues, useFixRequestForm } from './FixRequestForm';

interface GroupRelatedModalProps {
    open: boolean;
    onClose: () => void;
    onGrouped: () => void;
    orgId: string;
    appId: string;
    selectedIssues: IssueEntity[];
    viewType: TriageViewType;
    // When true, the combined issue is escalated to a fix request after merging
    escalate?: boolean;
}

interface GroupRelatedFormValues extends FixRequestFormValues {
    title?: string;
    description?: string;
}

export const GroupRelatedModal: React.FC<GroupRelatedModalProps> = ({
    open,
    onClose,
    onGrouped,
    orgId,
    appId,
    selectedIssues,
    viewType,
    escalate,
}) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<GroupRelatedFormValues>();
    const { nextVersion, fields, seedOnOpen, buildFormFieldsValues } = useFixRequestForm(appId);
    const selectedVersion = Form.useWatch('version', form) ?? nextVersion;

    // The combined issue must be the one with a fix request (if any) so that fix request is preserved; the others
    // are merged into it and deleted. Otherwise just keep the first selected.
    const target = selectedIssues.find((issue) => !!issue.fixRequest) ?? selectedIssues[0];
    const targetId = target?.id ?? '';
    const isMergeIntoFixRequest = !!target?.fixRequest;

    const isSaving = useIsLoading(TRY_GROUP_ISSUES, targetId);
    const saveError = useError(TRY_GROUP_ISSUES, targetId);
    const saveSucceeded = useUpdated(TRY_GROUP_ISSUES, targetId);

    React.useEffect(() => {
        if (saveSucceeded) {
            form.resetFields();
            onClose();
            onGrouped();
        }
    }, [saveSucceeded]);

    const onSubmit = (values: GroupRelatedFormValues) => {
        if (!target) {
            return;
        }
        dispatch(
            tryGroupIssues({
                orgId,
                appId,
                targetIssueId: target.id,
                sourceIssueIds: selectedIssues.filter((issue) => issue.id !== target.id).map((issue) => issue.id),
                title: values.title,
                description: values.description,
                ...(escalate ?
                    {
                        version: values.version ?? nextVersion,
                        formFieldsValues: buildFormFieldsValues(values),
                        viewType,
                    }
                :   {}),
            }),
        );
    };

    const modalTitle =
        escalate ? 'Group and escalate to fix request'
        : isMergeIntoFixRequest ? 'Merge into existing issue with fix request'
        : 'Group related issues';
    const okText =
        escalate ? 'Group and escalate'
        : isMergeIntoFixRequest ? 'Merge'
        : 'Group';
    const okDisabled = escalate && !selectedVersion;

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText={okText}
            okButtonProps={{
                disabled: okDisabled,
                style: {
                    backgroundColor: rwColors.primary,
                    borderColor: rwColors.primary,
                    color: runwayColors.white,
                    opacity: okDisabled ? 0.5 : undefined,
                },
            }}
            confirmLoading={isSaving}
            afterOpenChange={(isOpen) => {
                if (isOpen && escalate) {
                    seedOnOpen(form);
                }
            }}
            destroyOnClose
            width={600}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                requiredMark
                preserve={false}
                style={{ marginTop: 12 }}
            >
                {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
                <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
                    <Input placeholder="Title for the combined issue" />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <Input.TextArea
                        placeholder="Description for the combined issue"
                        autoSize={{ minRows: 3, maxRows: 8 }}
                    />
                </Form.Item>
                {escalate && fields}
            </Form>
        </Modal>
    );
};
